import { useState } from 'react';
import { Badge, Text } from '@mantine/core';
import type { ReactElement } from 'react';
import MegaGrid, { type MegaGridColumn, type MegaGridController } from '@/design/MegaGrid/MegaGrid';
import { useGlobalFilterSummary } from '@/features/global-filters/useGlobalFilterSummary';
import type { InteractionRecord } from '@/shared/analytics/AnalyticsTypes';
import type { VizAccent } from '@/design/VizAccents';
import { formatCount, formatTimestamp, humanUser } from '@/shared/analytics/MetricFormatting';
import InteractionRecordDrawer, { confidenceOf, difficultyColor, dimValue } from './InteractionRecordDrawer';

interface EvidenceTableProps {
    records: InteractionRecord[];
    accent: VizAccent;
    /** Persists this table's view state + enables saved views (scope per insight slice). */
    persistenceKey?: string;
    /** Overrides the default evidence columns (e.g. Spend's token-consumption cut). */
    columns?: MegaGridColumn<InteractionRecord>[];
    /** Paginates client-side at this page size instead of one scrolling table. */
    clientPageSize?: number;
    /** Seeds the grid's initial grouping (column keys), e.g. `['topic']` for session drift. */
    defaultGroupBy?: string[];
    /** External grid controller (e.g. to seed navigation-context filters); the caller owns view state when provided. */
    controller?: MegaGridController;
}

const COLUMNS: MegaGridColumn<InteractionRecord>[] = [
    {
        key: 'user',
        header: 'User',
        sortValue: (r) => humanUser(r.userId),
        render: (r) => (
            <Text size="xs" fw={600}>
                {humanUser(r.userId)}
            </Text>
        ),
    },
    {
        key: 'department',
        header: 'Department',
        sortValue: (r) => dimValue(r, 'user_department'),
        groupValue: (r) => dimValue(r, 'user_department'),
        filter: { type: 'enum', accessor: (r) => dimValue(r, 'user_department') },
        render: (r) => (
            <Text size="xs" c="dimmed">
                {dimValue(r, 'user_department')}
            </Text>
        ),
    },
    {
        key: 'when',
        header: 'When',
        sortValue: (r) => new Date(r.timestamp),
        render: (r) => (
            <Text size="xs" c="dimmed" ff="monospace">
                {formatTimestamp(r.timestamp)}
            </Text>
        ),
    },
    {
        key: 'model',
        header: 'Model',
        defaultHidden: true,
        sortValue: (r) => r.model,
        filter: { type: 'enum', accessor: (r) => r.model },
        render: (r) => (
            <Text size="xs" ff="monospace">
                {r.model}
            </Text>
        ),
    },
    {
        key: 'difficulty',
        header: 'Difficulty',
        sortValue: (r) => dimValue(r, 'difficulty'),
        filter: { type: 'enum', accessor: (r) => dimValue(r, 'difficulty') },
        render: (r) => (
            <Badge size="xs" radius="sm" variant="light" color={difficultyColor(dimValue(r, 'difficulty'))}>
                {dimValue(r, 'difficulty')}
            </Badge>
        ),
    },
    {
        key: 'category',
        header: 'Category',
        defaultHidden: true,
        sortValue: (r) => dimValue(r, 'task_category'),
        groupValue: (r) => dimValue(r, 'task_category'),
        render: (r) => <Text size="xs">{dimValue(r, 'task_category')}</Text>,
    },
    {
        key: 'task',
        header: 'Task',
        width: 240,
        render: (r) => (
            <Text size="xs" c="dimmed" truncate>
                {dimValue(r, 'task_subcategory')}
            </Text>
        ),
    },
    {
        key: 'out',
        header: 'Out',
        defaultHidden: true,
        align: 'right',
        sortValue: (r) => r.outputTokens,
        render: (r) => (
            <Text size="xs" ff="monospace">
                {formatCount(r.outputTokens)}
            </Text>
        ),
    },
    {
        key: 'conf',
        header: 'Conf.',
        defaultHidden: true,
        align: 'right',
        sortValue: (r) => r.dimensions['task_category']?.confidence ?? null,
        render: (r) => (
            <Text size="xs" ff="monospace" c="dimmed">
                {confidenceOf(r, 'task_category')}
            </Text>
        ),
    },
];

/** Dense, drill-down table of the raw supporting turns. Clicking a row opens the full classified record. */
export default function EvidenceTable({ records, accent, persistenceKey, columns = COLUMNS, clientPageSize, controller, defaultGroupBy }: EvidenceTableProps): ReactElement {
    const [selected, setSelected] = useState<InteractionRecord | null>(null);
    const externalFilters = useGlobalFilterSummary();

    return (
        <>
            <MegaGrid
                columns={columns}
                rows={records}
                externalFilters={externalFilters}
                getRowKey={(r) => r.id}
                onRowClick={(r) => setSelected(r)}
                controller={controller}
                persistenceKey={persistenceKey}
                searchAccessor={(r) => `${humanUser(r.userId)} ${r.model} ${dimValue(r, 'task_category')} ${dimValue(r, 'task_subcategory')} ${dimValue(r, 'user_department')}`}
                searchPlaceholder="Search prompts"
                striped
                density="comfortable"
                minWidth={760}
                maxHeight="60vh"
                clientPageSize={clientPageSize}
                defaultGroupBy={defaultGroupBy}
            />

            <InteractionRecordDrawer record={selected} onClose={() => setSelected(null)} accent={accent} />
        </>
    );
}
